import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isAxiosError } from 'axios'
import type { IErrorResponse } from '@/api/interfaces'
import type { FundingRequestListPrivateDto, FundingRequestPrivateDto } from '@/api/kubik/credit'
import { useCreate } from '@/api/kubik/credit'
import type { FundingRequestData } from '@/api/kubik/monolith'
import { mapApplyFinancingOfferHandlerOptionsToDto } from '@/components/composed/financing/helpers'
import type { TApplyFinancingOfferHandler } from '@/components/composed/financing/types'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { FinancingUxFeedbackModal } from '@/components/composed/modals/FinancingUxFeedbackModal/FinancingUxFeedbackModal'
import type { IFinancingUxFeedbackModalProps } from '@/components/composed/modals/FinancingUxFeedbackModal/types'
import { getLocation } from '@/logic/app/reducer'
import type { IApplyingFinancingOfferSuccessModalOptions } from '@/logic/financing/interfaces'
import { sendSubmitFormStatusEventWithUserInfo } from '@/logic/metrika/constants'
import { pushGtmEventOnSubmitOrder } from '@/logic/metrika/financing/gtmHelpers'
import type { EGtmBlock } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { getUserRegion } from '@/logic/userRegion'
import { ApplyingFinancingOfferSuccessModal } from '@/pages/Financing/components'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingProductId } from '@/types'
import { ESubmitFormStatusWithGtm } from '@/types'
import type { TUseApplyFinancingOfferResult } from './types'

addModalToWellKnown({ ApplyingFinancingOfferSuccessModal, FinancingUxFeedbackModal })

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
  userRegion: getUserRegion()(state),
  location: getLocation()(state),
})

export const useApplyFinancingOffer = (gtmBlockName?: EGtmBlock): TUseApplyFinancingOfferResult => {
  const { userGtmInfo, userRegion, location } = useSelector(mapState)
  const dispatch = useDispatch()
  const { mutateAsync: createFinancingOffer, isLoading, isSuccess } = useCreate()

  const sendSuccessGtmEvents = useCallback(
    (productIds: EFinancingProductId[], fundingRequests: FundingRequestPrivateDto[]) => {
      const firstProductId = productIds[0] as EFinancingProductId

      pushGtmEventOnSubmitOrder(
        userGtmInfo,
        userRegion.name,
        fundingRequests as Pick<FundingRequestData, 'id' | 'multiId'>[],
        productIds,
        gtmBlockName,
      )

      sendSubmitFormStatusEventWithUserInfo(firstProductId, ESubmitFormStatusWithGtm.success, userGtmInfo, location.pathname)
    },
    [location.pathname, userGtmInfo, userRegion.name, gtmBlockName],
  )

  const sendErrorGtmEvents = useCallback(
    (firstProductId: EFinancingProductId, errorMessage: string) => {
      sendSubmitFormStatusEventWithUserInfo(firstProductId, `err: ${errorMessage}`, userGtmInfo, location.pathname)
    },
    [location.pathname, userGtmInfo],
  )

  const openSuccessModal = useCallback(
    (requestId: number, productIds: EFinancingProductId[], isShowUxFeedback?: boolean) => {
      dispatch(
        openModal({
          options: {
            dialogId: 'ApplyingFinancingOfferSuccessModal',
            onCloseModal: () => {
              if (isShowUxFeedback) {
                dispatch(
                  openModal({
                    options: { dialogId: 'FinancingUxFeedbackModal', shouldBlockedScroll: false },
                    contentOptions: { isHasSkipButton: false } as IFinancingUxFeedbackModalProps['options'],
                  }),
                )
              }
            },
          },
          contentOptions: {
            requestId,
            prevFormValues: {
              productId: productIds[0],
            },
          } as IApplyingFinancingOfferSuccessModalOptions,
        }),
      )
    },
    [dispatch],
  )

  const applyFinancingOffer: TApplyFinancingOfferHandler = useCallback(
    async options => {
      const { withSuccessModal, isShowUxFeedback = false } = options
      const dto = mapApplyFinancingOfferHandlerOptionsToDto(options)

      try {
        const { data } = await createFinancingOffer(dto)
        const fundingRequests = data.fundingRequests ?? []
        const firstRequestId = fundingRequests?.[0]?.id

        sendSuccessGtmEvents(dto.productIds, fundingRequests)

        if (withSuccessModal && firstRequestId) {
          openSuccessModal(firstRequestId, dto.productIds, isShowUxFeedback)
        }

        return fundingRequests
      } catch (error) {
        if (isAxiosError<FundingRequestListPrivateDto & IErrorResponse>(error) && error.response) {
          const errorMessage = error.response.data.message
          const firstProductId = dto.productIds[0]

          if (firstProductId) {
            sendErrorGtmEvents(firstProductId, errorMessage)
          }

          throw new Error(errorMessage)
        }

        throw error
      }
    },
    [createFinancingOffer, openSuccessModal, sendErrorGtmEvents, sendSuccessGtmEvents],
  )

  return {
    applyFinancingOffer,
    openSuccessModal,
    isLoading,
    isSuccess,
  }
}
